import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { makePinKey, readAllPins, writeAllPins, PIN_STORAGE_KEY } from '../utils/pinUtils';

const TOGGLE_DEBOUNCE_MS = 400;

/**
 * Manage pinned (starred) combinations for a given fusion type (front/back).
 *
 * Stores a snapshot of the row at the moment of pinning. On subsequent
 * refreshes, if the same combination still exists in the fresh data, the
 * fresh values are used; otherwise we fall back to the snapshot and flag
 * the row as `_stale = true`.
 */
export const usePinnedRows = (fusionType) => {
  const [allPins, setAllPins] = useState(() => readAllPins());
  const lastToggleRef = useRef({ key: '', at: 0 });

  // Persist on change
  useEffect(() => {
    writeAllPins(allPins);
  }, [allPins]);

  // Sync changes across tabs
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === PIN_STORAGE_KEY) {
        setAllPins(readAllPins());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const pinsForType = allPins[fusionType] || {};

  const pinnedKeys = useMemo(() => new Set(Object.keys(pinsForType)), [pinsForType]);

  const isPinned = useCallback((row) => {
    if (!row) return false;
    const key = row._pinKey || makePinKey(row);
    if (!key) return false;
    return pinnedKeys.has(key);
  }, [pinnedKeys]);

  const togglePin = useCallback((row) => {
    if (!row) return;
    // Pinned rows carry the exact storage key — `makePinKey` can differ after
    // a refresh (qty / date formatting) and would otherwise add a second entry
    // or fail to remove the real pin.
    const key = row._pinKey || makePinKey(row);
    if (!key) return;

    const now = Date.now();
    if (lastToggleRef.current.key === key && now - lastToggleRef.current.at < TOGGLE_DEBOUNCE_MS) {
      return;
    }
    lastToggleRef.current = { key, at: now };

    setAllPins(prev => {
      const typePins = { ...(prev[fusionType] || {}) };
      if (typePins[key]) {
        delete typePins[key];
      } else {
        const { _pinned, _stale, _pinnedAt, _pinKey: _pk, ...clean } = row || {};
        typePins[key] = {
          snapshot: clean,
          pinnedAt: Date.now(),
        };
      }
      return { ...prev, [fusionType]: typePins };
    });
  }, [fusionType]);

  const clearAll = useCallback(() => {
    setAllPins(prev => ({ ...prev, [fusionType]: {} }));
  }, [fusionType]);

  /**
   * Split incoming fresh rows into a pinned section (with stale flags +
   * snapshots for missing ones) and a non-pinned remainder.
   */
  const partition = useCallback((freshRows) => {
    const rows = Array.isArray(freshRows) ? freshRows : [];
    const freshByKey = new Map();
    rows.forEach(r => { freshByKey.set(makePinKey(r), r); });

    // Earliest pinned first (top). Change to reverse if you prefer newest.
    const entries = Object.entries(pinsForType)
      .sort((a, b) => (a[1].pinnedAt || 0) - (b[1].pinnedAt || 0));

    const pinnedRows = entries.map(([key, data]) => {
      const fresh = freshByKey.get(key);
      const base = fresh || data.snapshot || {};
      return {
        ...base,
        id: `pin:${key}`,
        _pinned: true,
        _stale: !fresh,
        _pinnedAt: data.pinnedAt,
        _pinKey: key,
      };
    });

    const pinnedKeysSet = new Set(entries.map(([k]) => k));
    const nonPinnedRows = rows.filter(r => !pinnedKeysSet.has(makePinKey(r)));

    return { pinnedRows, nonPinnedRows };
  }, [pinsForType]);

  return {
    isPinned,
    togglePin,
    clearAll,
    partition,
    pinCount: pinnedKeys.size,
  };
};
